/*
 * <BeRadio> -- React wrapper for the .be-radio CSS component: the BeOS R5 radio
 * button. The knob is a bitmap (recessed ring + glossy blue sphere) shipped as
 * committed PNGs in src/assets/radio/ -- no runtime setup needed. Reconstructed
 * from rdef_src/libbe_r5/reconstructed/BRadioButton.Draw.match.cpp; see
 * components/radio.css for provenance.
 *
 * Renders a real <input type="radio"> inside a <label> (semantic, accessible).
 * Group radios by sharing `name`.
 *
 *   <BeRadio name="speed" value="fast" checked={v === "fast"}
 *            onChange={() => setV("fast")}>Fast</BeRadio>
 */

import type { ReactNode } from "react";
import { type BeProps, beComponent } from "./create.tsx";
import { pixelText } from "./pixel-text.tsx";

export type BeRadioProps = BeProps<
	"input",
	{
		/** The radio label. */
		children?: ReactNode;
	}
>;

/** The ref lands on the <input>, not the <label> -- see create.tsx. */
export const BeRadio = beComponent<BeRadioProps, HTMLInputElement>("BeRadio", (props, be) => {
	const { children, scale, className, style, type: _type, ...rest } = props;
	const { ref, ...root } = be("be-radio");

	return (
		<label {...root}>
			<input type="radio" className="be-radio__input" ref={ref} {...rest} />
			<span className="be-radio__knob" />
			{/* outer span keeps the class: it paints the focus underline gradient. */}
			{children != null && <span className="be-radio__label">{pixelText(children)}</span>}
		</label>
	);
});
